/**
 * Absorption-at-level markers ("ABS PW" dots under / above candles).
 *
 * Detected by the engine (utt-engine/src/absorption) on raw SCID prints at the
 * close of each base bar; this store only holds and routes them.
 *
 * Contract (utt-engine):
 * - REST GET  {api}/absorption-markers/{symbol}?days=N -> { markers, ready, calibration, alertsEnabled }
 * - REST GET/POST {api}/absorption/settings            -> { alertsEnabled }
 * - Socket "absorption-marker"          -> AbsorptionMarker (one new detection)
 * - Socket "absorption-markers-loaded"  -> { symbol, markers } (boot replay finished)
 *
 * Visibility and colours live in levelsStore (`absorption`), so they persist
 * with the other level toggles.
 */
import { create } from 'zustand';
import { wsService } from '../services/WebSocketService';
import { getApiBaseUrl } from '../config/ports';
import { normalizeToBase } from '../utils/symbolUtils';

export type AbsorptionDirection = 'bullish' | 'bearish';

export interface AbsorptionCalibrationLine {
  /** Level code, or "ALL" when this level type had too few events of its own. */
  scope: string;
  heldPct: number;
  n: number;
  touchPct: number;
  touchN: number;
  closeBackPct: number | null;
  closeBackN: number;
  thresholdPts: number;
  sessions: number;
  predictive: boolean;
  /** "held 65% (n=638) vs 60% plain touch". */
  text: string;
}

export interface AlsoAt {
  code: string;
  price: number;
}

export interface AbsorptionMarker {
  /** "{symbol}:{barStart}:{direction}:{code}". */
  id: string;
  symbol: string;
  /** Base bar start, ms. */
  timestamp: number;
  /** Base bar length, ms. */
  barMs: number;
  direction: AbsorptionDirection;
  levelCode: string;
  levelName: string;
  levelPrice: number;
  open: number;
  high: number;
  low: number;
  close: number;
  aggressiveVolume: number;
  barSideVolume: number;
  baselineMean: number;
  z: number;
  percentile: number | null;
  closeBack: number;
  penetration: number;
  alsoAt: AlsoAt[];
  calibration: AbsorptionCalibrationLine | null;
}

/** Newest markers kept per base symbol. */
export const MAX_ABSORPTION_MARKERS = 1500;

export function isAbsorptionMarker(m: unknown): m is AbsorptionMarker {
  if (!m || typeof m !== 'object') return false;
  const c = m as Partial<AbsorptionMarker>;
  return (
    typeof c.id === 'string' &&
    typeof c.symbol === 'string' &&
    typeof c.timestamp === 'number' &&
    typeof c.levelCode === 'string' &&
    typeof c.levelPrice === 'number' &&
    typeof c.high === 'number' &&
    typeof c.low === 'number' &&
    (c.direction === 'bullish' || c.direction === 'bearish')
  );
}

/** Upsert by id, keep ascending by time, cap (newest kept). */
export function mergeAbsorption(existing: AbsorptionMarker[], incoming: AbsorptionMarker[]): AbsorptionMarker[] {
  const byId = new Map(existing.map((m) => [m.id, m]));
  for (const m of incoming) if (isAbsorptionMarker(m)) byId.set(m.id, m);
  const next = [...byId.values()].sort((a, b) => a.timestamp - b.timestamp);
  return next.length > MAX_ABSORPTION_MARKERS ? next.slice(next.length - MAX_ABSORPTION_MARKERS) : next;
}

interface AbsorptionState {
  /** Keyed by base symbol (NQ, MNQ, ES, MES). */
  bySymbol: Record<string, AbsorptionMarker[]>;
  alertsEnabled: boolean;
  /** Engine says whether the measured hold rate beat both controls out of sample. */
  predictive: boolean | null;

  addMarkers: (markers: AbsorptionMarker[]) => void;
  replaceForSymbol: (symbol: string, markers: AbsorptionMarker[]) => void;
  fetchMarkers: (symbol: string) => Promise<void>;
  fetchSettings: () => Promise<void>;
  setAlertsEnabled: (on: boolean) => Promise<void>;
  initialize: () => void;
  teardown: () => void;
}

let handlersRegistered = false;
let retryTimer: ReturnType<typeof setInterval> | null = null;
let warned = false;
/** Bumped by every alert-setting write, so a slower read cannot undo a click. */
let alertWrites = 0;

const onMarker = (m: unknown): void => {
  if (isAbsorptionMarker(m)) useAbsorptionStore.getState().addMarkers([m]);
};

const onLoaded = (payload: unknown): void => {
  const p = payload as { symbol?: unknown; markers?: unknown };
  if (!p || typeof p.symbol !== 'string' || !Array.isArray(p.markers)) return;
  useAbsorptionStore.getState().replaceForSymbol(p.symbol, p.markers.filter(isAbsorptionMarker));
};

function tryRegister(): boolean {
  if (!wsService.isConnected()) return false;
  wsService.on('absorption-marker', onMarker);
  wsService.on('absorption-markers-loaded', onLoaded);
  handlersRegistered = true;
  return true;
}

export const useAbsorptionStore = create<AbsorptionState>((set, get) => ({
  bySymbol: {},
  alertsEnabled: false,
  predictive: null,

  addMarkers: (markers) =>
    set((s) => {
      const next = { ...s.bySymbol };
      for (const m of markers) {
        if (!isAbsorptionMarker(m)) continue;
        const base = normalizeToBase(m.symbol);
        next[base] = mergeAbsorption(next[base] ?? [], [m]);
      }
      return { bySymbol: next };
    }),

  // Merge, not replace: a live marker can land between the engine's hand-over
  // and this batch arriving, and must not be dropped.
  replaceForSymbol: (symbol, markers) =>
    set((s) => {
      const base = normalizeToBase(symbol);
      return { bySymbol: { ...s.bySymbol, [base]: mergeAbsorption(s.bySymbol[base] ?? [], markers) } };
    }),

  fetchMarkers: async (symbol) => {
    if (!symbol) return;
    try {
      const res = await fetch(`${getApiBaseUrl()}/absorption-markers/${encodeURIComponent(symbol)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const markers: AbsorptionMarker[] = Array.isArray(body?.markers) ? body.markers.filter(isAbsorptionMarker) : [];
      // Merge, not replace: a live marker may have landed while this was in flight.
      set((s) => {
        const base = normalizeToBase(symbol);
        return {
          bySymbol: { ...s.bySymbol, [base]: mergeAbsorption(s.bySymbol[base] ?? [], markers) },
          alertsEnabled: typeof body?.alertsEnabled === 'boolean' ? body.alertsEnabled : s.alertsEnabled,
          predictive: typeof body?.calibration?.predictive === 'boolean' ? body.calibration.predictive : s.predictive,
        };
      });
    } catch (e) {
      if (!warned) {
        warned = true;
        console.warn('[Absorption] markers unavailable (engine not deployed yet?)', e);
      }
    }
  },

  fetchSettings: async () => {
    const seen = alertWrites;
    try {
      const res = await fetch(`${getApiBaseUrl()}/absorption/settings`);
      if (!res.ok) return;
      const body = await res.json();
      if (seen === alertWrites && typeof body?.alertsEnabled === 'boolean') set({ alertsEnabled: body.alertsEnabled });
    } catch {
      /* engine not reachable: keep the current value */
    }
  },

  setAlertsEnabled: async (on) => {
    alertWrites += 1;
    const prev = get().alertsEnabled;
    set({ alertsEnabled: on });
    try {
      const res = await fetch(`${getApiBaseUrl()}/absorption/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alertsEnabled: on }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || body?.success === false) throw new Error(body?.error ?? `HTTP ${res.status}`);
    } catch (e) {
      console.warn('[Absorption] could not save alert setting', e);
      set({ alertsEnabled: prev });
    }
  },

  initialize: () => {
    if (handlersRegistered || tryRegister() || retryTimer !== null) return;
    let attempts = 0;
    retryTimer = setInterval(() => {
      attempts += 1;
      if (tryRegister() || attempts >= 60) {
        if (retryTimer !== null) clearInterval(retryTimer);
        retryTimer = null;
      }
    }, 1000);
  },

  teardown: () => {
    if (retryTimer !== null) clearInterval(retryTimer);
    retryTimer = null;
    if (!handlersRegistered) return;
    handlersRegistered = false;
    wsService.off('absorption-marker', onMarker);
    wsService.off('absorption-markers-loaded', onLoaded);
  },
}));

const fmtVol = (v: number) => v.toLocaleString('en-US');

/** Tooltip title + lines for one marker. */
export function absorptionTooltip(m: AbsorptionMarker): { title: string; lines: string[] } {
  const side = m.direction === 'bullish' ? 'sellers absorbed at support' : 'buyers absorbed at resistance';
  const aggr = m.direction === 'bullish' ? 'sold at the bid at/below the level' : 'bought at the ask at/above the level';
  const lines = [
    `${m.levelName} ${m.levelPrice.toFixed(2)}: ${side}`,
    `Aggressive volume ${fmtVol(m.aggressiveVolume)} ${aggr} (z ${m.z.toFixed(1)} vs ${Math.round(m.baselineMean)} avg per bar)`,
    `Close ${m.close.toFixed(2)}: ${
      m.closeBack >= 0
        ? `${m.closeBack.toFixed(2)} ${m.direction === 'bullish' ? 'above' : 'below'}`
        : `${Math.abs(m.closeBack).toFixed(2)} past`
    } the level, wick ${m.penetration.toFixed(2)} through`,
  ];
  if (m.alsoAt.length > 0) lines.push(`Also at: ${m.alsoAt.map((a) => `${a.code} ${a.price.toFixed(2)}`).join(', ')}`);
  const c = m.calibration;
  if (c) {
    lines.push(`ABS ${c.scope === 'ALL' ? 'all levels' : m.levelCode} · ${c.text}`);
    const ctl = c.closeBackPct !== null ? ` · close-back without the volume ${Math.round(c.closeBackPct)}%` : '';
    lines.push(
      `${c.thresholdPts}pt reversal before ${c.thresholdPts}pt through, ${c.sessions} sessions${ctl}. ${
        c.predictive ? 'Held up out of sample.' : 'Not predictive out of sample.'
      }`,
    );
  } else {
    lines.push('No calibration for this instrument.');
  }
  return { title: `ABS ${m.levelCode} ${m.levelPrice.toFixed(2)}`, lines };
}
